import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../api/axiosInstance";

// ------------------- Thunks -------------------

export const fetchAttributeValues = createAsyncThunk("attributeValues/fetchAll", async ({ attributeId, query = "" }, thunkAPI) => {
  try {
    const res = await api.get(`/dashboard/attributes/${attributeId}/values${query}`);
    return res.data.data.values;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data || error.message);
  }
});

export const createAttributeValue = createAsyncThunk(
  "attributeValues/create",
  async ({ attributeId, payload }, thunkAPI) => {
    try {
      const response = await api.post(`/dashboard/attributes/${attributeId}/values`, payload);
      return response.data.data.value;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getAttributeValue = createAsyncThunk(
  "attributeValues/get",
  async (valueId, thunkAPI) => {
    try {
      const response = await api.get(`/dashboard/attributes/values/${valueId}`);
      return response.data.data.value;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateAttributeValue = createAsyncThunk(
  "attributeValues/update",
  async ({ valueId, payload }, thunkAPI) => {
    try {
      const response = await api.put(`/dashboard/attributes/values/${valueId}`, payload);
      return response.data.data.value;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteAttributeValue = createAsyncThunk(
  "attributeValues/delete",
  async (valueId, thunkAPI) => {
    try {
      await api.delete(`/dashboard/attributes/values/${valueId}`);
      return valueId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ------------------- Slice -------------------

const attributeValuesSlicer = createSlice({
  name: "attributeValues",
  initialState: {
    data: [],
    currentValue: null,
    loading: {
      fetch: false,
      create: false,
      get: false,
      update: false,
      delete: false,
    },
    error: null,
    apiErrors: {},
    successMessage: "",
  },
  reducers: {
    clearAttributeValuesState(state) {
      state.data = [];
      state.currentValue = null;
      state.loading = { fetch: false, create: false, get: false, update: false, delete: false };
      state.error = null;
      state.apiErrors = {};
      state.successMessage = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // --- Fetch
      .addCase(fetchAttributeValues.pending, (state) => {
        state.loading.fetch = true;
        state.error = null;
      })
      .addCase(fetchAttributeValues.fulfilled, (state, action) => {
        state.loading.fetch = false;
        state.data = action.payload;
      })
      .addCase(fetchAttributeValues.rejected, (state, action) => {
        state.loading.fetch = false;
        state.error = true;
        state.apiErrors = action.payload?.errors || {};
      })
      // Create
      .addCase(createAttributeValue.pending, (state) => {
        state.loading.create = true;
        state.apiErrors = {};
      })
      .addCase(createAttributeValue.fulfilled, (state, action) => {
        state.loading.create = false;
        state.data.push(action.payload);
        state.successMessage = action.payload?.message || "";
      })
      .addCase(createAttributeValue.rejected, (state, action) => {
        state.loading.create = false;
        state.apiErrors = action.payload?.errors || {};
      })

      // Get
      .addCase(getAttributeValue.pending, (state) => {
        state.loading.get = true;
        state.currentValue = null;
      })
      .addCase(getAttributeValue.fulfilled, (state, action) => {
        state.loading.get = false;
        state.currentValue = action.payload;
      })
      .addCase(getAttributeValue.rejected, (state, action) => {
        state.loading.get = false;
        state.error = true;
      })

      // Update
      .addCase(updateAttributeValue.pending, (state) => {
        state.loading.update = true;
        state.apiErrors = {};
      })
      .addCase(updateAttributeValue.fulfilled, (state, action) => {
        state.loading.update = false;
        const index = state.data.findIndex(v => v.id === action.payload.id);
        if (index !== -1) state.data[index] = action.payload;
        state.successMessage = action.payload?.message || "";
      })
      .addCase(updateAttributeValue.rejected, (state, action) => {
        state.loading.update = false;
        state.apiErrors = action.payload?.errors || {};
      })

      // Delete
      .addCase(deleteAttributeValue.pending, (state) => {
        state.loading.delete = true;
      })
      .addCase(deleteAttributeValue.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.data = state.data.filter((v) => v.id !== action.payload);
        state.successMessage = action.payload?.message || "";
      })
      .addCase(deleteAttributeValue.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = true;
      });
  },
});

export const { clearAttributeValuesState } = attributeValuesSlicer.actions;
export default attributeValuesSlicer.reducer;
